import {
  usePaymentCreatePaymentAccount,
  usePaymentGetPaymentAccount,
  usePaymentGetStripeLogin,
} from "@crowdcoursing/api/openapi";
import { Button, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { valueOrFallback } from "~utils/sanitizeDto";

import { type State } from "../../state/state";
import { ExternalLink } from "../../ui/atoms/ExternalLink";

// Centralized strings for internationalization
const tSetupToRecieve = "You are all setup to receive payments.";
const tYouCanSeePayments = "You can see payments and update your settings on your";
const tLoading = "Loading...";
const tOpenStripe = "Open Stripe settings in a new window";
const tAlmostComplete = "Your payment account is almost complete.";
const tFinishSetup = "Finish Setup";
const tWePartnerWith = "We partner with";
const tToProcessPayments = "to securely process payments.";
const tSetupPayments = "Setup Payments";
const tStripe = "Stripe";
const tOpenStripeHomePage = "open Stripe home page";
const tLoadingGeneric = "Loading...";

function useRedirectToStripeProfileCreation() {
  const { mutateAsync } = usePaymentCreatePaymentAccount({
    onSettled(data) {
      if (!data?.url) {
        console.error("(PaymentAccountForm)(usePaymentApi)(redirectToStriptProfileCreation) Missing url");
      }
      if (data?.url) {
        // send them off to Stripe
        window.location.assign(data.url);
      }
    },
  });

  return useCallback(
    async function (userId: string) {
      return mutateAsync({ pathParams: { userId } });
    },
    [mutateAsync],
  );
}

/**
 * User has a fully configured payment account
 * @deprecated We are phasing out `components/**`
 */
const PaymentProfileReady = () => {
  const userId = useSelector((state: State) =>
    valueOrFallback(state.user?.id, "NO_USER_ID", "(PaymentAccountForm)(usePaymentApi) state.user.id is undefined"),
  );
  const { data, status } = usePaymentGetStripeLogin({ pathParams: { userId } });
  if (status === "pending" || !data?.url) {
    return <>{tLoading}</>;
  }
  return (
    <>
      <Typography variant="body2">
        {tSetupToRecieve}
        <br />
        <br />
        {tYouCanSeePayments}&nbsp;
        <ExternalLink title={tOpenStripe} url={data.url}>
          {tStripe}
        </ExternalLink>
      </Typography>
    </>
  );
};

/**
 * User has a partially configured payment account
 * @deprecated We are phasing out `components/**`
 */
const PaymentProfileIncomplete = () => {
  const userId = useSelector((state: State) =>
    valueOrFallback(state.user?.id, "NO_USER_ID", "(PaymentAccountForm)(usePaymentApi) state.user.id is undefined"),
  );
  const { data, status, mutateAsync } = usePaymentCreatePaymentAccount();

  useEffect(
    function () {
      mutateAsync({ pathParams: { userId } });
    },
    [userId, mutateAsync],
  );

  if (status === "pending" || !data?.url) {
    return <>{tLoading}</>;
  }

  return (
    <Typography variant="body2">
      {tAlmostComplete}&nbsp;
      <br /> <br />
      <ExternalLink title={tFinishSetup} url={data.url}>
        {tFinishSetup}
      </ExternalLink>
    </Typography>
  );
};

/**
 * User has no payment account
 * @deprecated We are phasing out `components/**`
 */
const PaymentProfileAbsent = () => {
  const userId = useSelector((state: State) =>
    valueOrFallback(state.user?.id, "NO_USER_ID", "(PaymentAccountForm)(usePaymentApi) state.user.id is undefined"),
  );

  const redirectToStripeProfileCreation = useRedirectToStripeProfileCreation();

  const [disabled, setDisabled] = useState(false);
  const onClick = async () => {
    setDisabled(true);
    await redirectToStripeProfileCreation(userId);
  };

  return (
    <Typography variant="body2">
      {tWePartnerWith}&nbsp;
      <ExternalLink title={tOpenStripeHomePage} url="https://stripe.com">
        {tStripe}
      </ExternalLink>
      &nbsp;{tToProcessPayments}
      <br />
      <br />
      <Button disabled={disabled} onClick={onClick}>
        {tSetupPayments}
      </Button>
    </Typography>
  );
};

/** @deprecated We are phasing out `components/**` */
export const PaymentAccountForm = () => {
  const userId = useSelector((state: State) =>
    valueOrFallback(state.user?.id, "NO_USER_ID", "(PaymentAccountForm)(usePaymentApi) state.user.id is undefined"),
  );
  const { data, status } = usePaymentGetPaymentAccount({ pathParams: { userId } });

  if (status === "pending" || !data) {
    return <>{tLoadingGeneric}</>;
  } else if (!data.accountCreated) {
    return <PaymentProfileAbsent />;
  } else if (data.accountCreated && !data.payoutsEnabled) {
    return <PaymentProfileIncomplete />;
  } else {
    return <PaymentProfileReady />;
  }
};

/**
 * Implements the stripe profile creation
 * {@link https://stripe.com/docs/api/account_links/create#create_account_link-refresh_url refresh_url}
 *
 * > The URL that the user will be redirected to if the account link is no
 * > longer valid. Your refresh_url should trigger a method on your server to
 * > create a new account link using this API, with the same parameters, and
 * > redirect the user to the new account link.
 *
 * @deprecated We are phasing out `components/**`
 */
export const ProfileRefresh = () => {
  const userId = useSelector((state: State) =>
    valueOrFallback(state.user?.id, "NO_USER_ID", "(PaymentAccountForm)(usePaymentApi) state.user.id is undefined"),
  );

  const redirectToStripeProfileCreation = useRedirectToStripeProfileCreation();

  useEffect(() => {
    redirectToStripeProfileCreation(userId);
  }, [redirectToStripeProfileCreation, userId]);

  return null;
};
